/**
 * Cart page + drawer: drive the unified quantity ladder and the mobile sticky
 * checkout bar from the live cart.
 *
 * One component now covers what used to be two stacked bars (a free-shipping
 * bar and a separate volume-tier bar) that asked the shopper for two different
 * numbers ten dollars apart. Markup is identical on /cart and in the drawer,
 * so this file is the only place the thresholds are expressed.
 *
 * Driven off /cart.js and re-run whenever either surface re-renders, so it
 * survives quantity changes, removals, variant edits and drawer opens.
 */
(function (window, document) {
  'use strict';

  /* Stops in ITEM COUNT: the automatic quantity discounts.

     Reverted 2026-08-29 from the spend ladder ($50/15%, $85/20%, $125/25%) back to
     count-based tiers. 18 months of orders bunch at 2 and 3 ITEMS and show no bunching
     at any dollar threshold, because every SKU is $23-60 so a cart total can only land
     on multiples of a unit price, which makes a spend threshold unaimable here.

     A 4th stop (4 items / 35% off) shipped 2026-08-29 and was pulled 2026-09-09: four
     rungs on one rail read as a pricing table rather than a single next step, and the
     shopper could not tell which number they were being asked for. Two rungs plus a
     "biggest saving" end state is the whole offer. Do NOT re-add a rung here without
     first creating the matching Shopify automatic discount.

     There is NO shipping stop here: as of 2026-08-27 the Domestic zone is a single
     unconditional $0.00 rate, so shipping is free on every US order. Keep these stops
     in lockstep with the Shopify automatic discounts (Admin > Discounts) or the bar
     lies. Counts, not cents, see unitsOf() below. */
  var STOPS = [
    { qty: 2, pct: 20, msg: 'to unlock 20% off', badge: 'UNLOCK 20% OFF' },
    { qty: 3, pct: 30, msg: 'to unlock 30% off', badge: 'UNLOCK 30% OFF' }
  ];

  /* Published so the PDP set builder (snippets/ajluxe-set-builder.liquid) can preview the
     tier a multi-necklace basket will hit WITHOUT keeping its own copy of these numbers.
     One definition, two consumers. The set builder degrades to "no preview" if this is
     ever absent, so removing it breaks nothing silently. */
  window.AJLuxeLadderStops = STOPS;

  /* The drawer upsell used to hardcode "UNLOCK 15% OFF" and a link to the
     initial necklace no matter what was in the cart, so a $137 cart already on
     the 25% tier was still being offered 15%, and an earrings-only cart was
     told to "add a second initial". Both now follow the cart. */
  var PERSONALIZED = /initial|letter|monogram|nameplate/i;

  var pending = null;

  function el(sel, root) {
    return (root || document).querySelector(sel);
  }

  function money(cents) {
    // Match the theme's formatter when it is available so currency apps agree.
    if (window.wpbingo && wpbingo.Currency && wpbingo.strings) {
      return wpbingo.Currency.formatMoney(cents, wpbingo.strings.moneyFormat || '${{amount}}');
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * Every stop is measured against the ITEM COUNT, which is exactly what
   * Shopify's DiscountMinimumQuantity requirement evaluates.
   *
   * This is why the tiers moved off spend: item_count cannot be dragged
   * backwards by a discount the way total_price can, so the bar can never
   * walk backwards the moment a tier fires — the whole class of bug the old
   * original_total_price guard existed to dodge simply does not arise.
   *
   * There is no shipping stop left to reconcile: shipping is free on every
   * US order regardless of subtotal. If a paid shipping rate is ever
   * reintroduced it needs its own money-based stop and must read
   * cart.total_price, not this.
   */
  function unitsOf(cart) {
    return typeof cart.item_count === 'number' ? cart.item_count : 0;
  }

  function nextStop(units) {
    for (var i = 0; i < STOPS.length; i++) {
      if (units < STOPS[i].qty) return STOPS[i];
    }
    return null;
  }

  /* Stops sit at even intervals so the rail is evenly divided regardless of
     how many tiers there are. The fill interpolates within whichever segment
     the cart is in. */
  function fillPercent(units) {
    var seg = 100 / STOPS.length;
    for (var i = 0; i < STOPS.length; i++) {
      if (units < STOPS[i].qty) {
        var floor = i === 0 ? 0 : STOPS[i - 1].qty;
        var within = (units - floor) / (STOPS[i].qty - floor);
        if (within < 0) within = 0;
        return (i * seg) + (within * seg);
      }
    }
    return 100;
  }

  /* "1 more piece" / "2 more pieces" — the ladder now asks for a countable
     thing, so it must never render a dollar amount. */
  function pieces(n) {
    return n + (n === 1 ? ' more piece' : ' more pieces');
  }

  /* ------------------------------------------------------------------
     Write through the EXISTING text node, never replace it.

     `el.textContent = x` and `el.innerHTML = x` both remove the old child
     nodes and insert new ones, which the browser reports as a childList
     mutation. Clarity's replay player applies the insert but not the matching
     remove, so a value written that way replays with every value the element
     has ever held, stacked end to end: the bag badge reading "22222221" and
     the price reading "$41.59$41.59$41.59..." in a recording are eight cart
     updates piled into one node, not something a shopper ever saw.

     Editing nodeValue on the node that is already there is a characterData
     mutation instead: one node, one value, nothing for the player to stack.
     Live rendering is identical either way, so this costs the storefront
     nothing and makes the recordings legible. See memory
     ajluxe-clarity-replay-text-duplication-artifact-sep13.

     Keep it that way. Any new copy on these surfaces should go through
     setText() and, if it needs emphasis, through a skeleton that already
     exists in the Liquid rather than an innerHTML string. */
  function setText(node, value) {
    if (!node) return;
    var first = node.firstChild;
    if (first && first.nodeType === 3 && !first.nextSibling) {
      if (first.nodeValue !== value) first.nodeValue = value;
      return;
    }
    // Only reached if the markup shipped without a text node to write into.
    node.textContent = value;
  }

  function renderLadder(units) {
    var wraps = document.querySelectorAll('[data-aj-ladder]');
    for (var w = 0; w < wraps.length; w++) {
      var wrap = wraps[w];
      var fill = el('[data-aj-ladder-fill]', wrap);
      var msg = el('[data-aj-ladder-msg]', wrap);
      var stops = wrap.querySelectorAll('[data-aj-stop]');

      if (fill) fill.style.width = fillPercent(units).toFixed(2) + '%';

      for (var s = 0; s < stops.length; s++) {
        var at = parseInt(stops[s].getAttribute('data-aj-stop'), 10);
        stops[s].classList.toggle('is-done', units >= at);
      }

      var next = nextStop(units);
      wrap.classList.toggle('is-complete', !next);
      if (!msg) continue;

      /* Both states are lead + emphasis + tail, so one skeleton shipped in the
         Liquid covers them and the structure never has to be rewritten. */
      var lead = el('[data-aj-ladder-lead]', msg);
      var strong = el('[data-aj-ladder-strong]', msg);
      var tail = el('[data-aj-ladder-tail]', msg);
      if (!lead || !strong || !tail) continue; // markup predates the skeleton

      if (next) {
        setText(lead, 'Add ');
        setText(strong, pieces(next.qty - units));
        setText(tail, ' ' + next.msg);
      } else {
        /* Do NOT put a free-shipping claim in this line. Domestic shipping is a
           single unconditional $0.00 rate since 2026-08-27, so it is not something
           the ladder unlocks — saying so implies the tier earned it and muddles
           what the shopper actually just achieved. */
        setText(lead, 'You’ve unlocked our biggest saving: ');
        setText(strong, '30% off');
        setText(tail, '');
      }
    }
  }

  function hasPersonalized(cart) {
    return (cart.items || []).some(function (item) {
      if (PERSONALIZED.test(item.handle || '')) return true;
      var props = item.properties || {};
      for (var k in props) {
        if (Object.prototype.hasOwnProperty.call(props, k) && PERSONALIZED.test(k)) return true;
      }
      return false;
    });
  }

  function renderUpsell(cart, units) {
    var boxes = document.querySelectorAll('[data-aj-upsell]');
    for (var b = 0; b < boxes.length; b++) renderUpsellInto(boxes[b], cart, units);
  }

  function renderUpsellInto(box, cart, units) {
    // Nothing left to earn, or nothing in the bag: say nothing rather than
    // offering a tier the shopper has already passed.
    var next = nextStop(units);
    if (!next || !cart.item_count) {
      box.setAttribute('hidden', '');
      return;
    }

    var badge = el('[data-aj-upsell-badge]', box);
    var head = el('[data-aj-upsell-headline]', box);
    var sub = el('[data-aj-upsell-sub]', box);
    var cta = el('[data-aj-upsell-cta]', box);

    setText(badge, next.badge);
    setText(sub, 'You\u2019re ' + pieces(next.qty - units) + ' away.');

    if (hasPersonalized(cart)) {
      setText(head, 'Add a second initial & save');
      if (cta) {
        setText(cta, 'ADD ANOTHER LETTER \u2192');
        cta.setAttribute('href', '/products/heart-initial-necklace');
      }
    } else {
      setText(head, 'Add one more piece & save');
      if (cta) {
        setText(cta, 'SHOP BEST SELLERS \u2192');
        cta.setAttribute('href', '/collections/best-sellers');
      }
    }
    box.removeAttribute('hidden');
  }

  function renderSticky(cart) {
    /* Two surfaces share this: the /cart bar (submits #cart-form) and the browse-page bar
       on collection and product pages (snippets/ajluxe-cart-sticky.liquid, a plain link to
       checkout). Both are hidden while the cart is empty, so a shopper who has added
       nothing never sees either one.

       Every field goes through setText()/setAttribute, never appended, so re-running
       this on each cart change can only ever replace what is on screen. */
    var bars = document.querySelectorAll('[data-aj-cart-sticky]');
    var units = unitsOf(cart);
    var first = cart.items && cart.items.length ? cart.items[0] : null;

    for (var i = 0; i < bars.length; i++) {
      var bar = bars[i];
      if (!cart.item_count) {
        bar.setAttribute('hidden', '');
        continue;
      }
      bar.removeAttribute('hidden');

      var price = bar.querySelector('[data-aj-sticky-price]');
      setText(price, money(cart.total_price));

      /* Everything below is browse-bar only; absent on /cart, where the item list,
         the thumbnails and the ladder are all already on screen. */
      var count = bar.querySelector('[data-aj-sticky-count]');
      setText(count, String(cart.item_count));

      var thumb = bar.querySelector('[data-aj-sticky-thumb]');
      var img = bar.querySelector('[data-aj-sticky-img]');
      var src = first ? (first.image || first.featured_image && first.featured_image.url) : null;
      if (img && thumb) {
        if (src) {
          /* Shopify CDN sizing param — ask for the 2x of the 48px slot, not the
             2048px master the cart object hands back. */
          img.setAttribute('src', src.split('?')[0] + '?width=96');
          img.setAttribute('alt', first.product_title || '');
          thumb.removeAttribute('hidden');
        } else {
          thumb.setAttribute('hidden', '');
        }
      }

      /* The nudge is the whole reason this bar beats a bare count: it asks for the next
         tier using the SAME stops as the cart ladder, so the two can never quote
         different numbers. No stop left = the shopper is already at the best price. */
      var nudge = bar.querySelector('[data-aj-sticky-nudge]');
      if (nudge) {
        var next = nextStop(units);
        if (next) {
          setText(nudge, 'Add ' + pieces(next.qty - units) + ' for ' + next.pct + '% off');
          nudge.classList.remove('is-complete');
        } else {
          setText(nudge, STOPS[STOPS.length - 1].pct + '% off applied at checkout');
          nudge.classList.add('is-complete');
        }
      }
    }
  }

  /* On /cart, the in-flow Checkout button (.cart-modal__btn) sits right above the
     Shop/express-pay wallet row/PayPal/Google Pay row, and the fixed sticky bar below (the
     non-browse .aj-cart-sticky) used to show unconditionally the moment the cart
     had items -- so a shopper near the top of a short cart saw THREE checkout
     CTAs in a few hundred pixels. renderSticky() above still gates purely on
     cart.item_count (shared with the browse-page bar, which has no in-flow
     button to defer to); this gate runs after it and hides the /cart-page bar
     again for as long as the in-flow button is still on screen, so the fixed
     bar only earns its keep once the "real" Checkout button has scrolled away. */
  var lastCart = null;
  var primaryCheckoutVisible = true;
  var primaryGateActive = false;

  /* The drawer's item list (.drawer__inner) is position:absolute so it can sit
     below the ladder/upsell block (.discount-progress-wrapper), which is itself
     a variable height -- the message text can be one or two lines, and the
     upsell box only shows sometimes. A hardcoded CSS top left a gap when that
     block was shorter than assumed (or, before that fix, overlapped the item
     image when it was taller). Measure the real height on every render instead
     of guessing a fixed pixel value in CSS. */
  function syncDrawerInnerOffset() {
    var drawer = document.querySelector('.js-drawer.drawer');
    if (!drawer) return;
    var wrap = drawer.querySelector('.discount-progress-wrapper');
    var inner = drawer.querySelector('.drawer__inner');
    if (!wrap || !inner) return;
    var wrapRect = wrap.getBoundingClientRect();
    var drawerRect = drawer.getBoundingClientRect();
    var offset = Math.round(wrapRect.bottom - drawerRect.top);
    if (offset > 0) inner.style.top = offset + 'px';
  }

  /* CSS max-height:100dvh (site-template.liquid) is meant to cap the drawer
     at the REAL visible viewport, not the taller 100vh that hides behind
     Safari's address bar. In theory dvh already does this, but iOS Safari
     has a long history of dvh/svh/lvh inconsistencies specifically for
     position:fixed elements -- confirmed live 2026-09-28: CSS dvh alone
     still left a blank gap below the checkout button on a real iPhone even
     though desktop and every emulated mobile size tested correctly. window.
     innerHeight is unambiguous in every browser, so measure it directly and
     apply it as an inline max-height (desktop also gets a matching height,
     since that's a fixed panel, not a shrink-wrap one). Re-measure on
     resize/orientationchange too, since Safari's innerHeight itself changes
     as the address bar shows and hides during scroll. */
  function syncDrawerMaxHeight() {
    var drawer = document.querySelector('.js-drawer.drawer');
    if (!drawer) return;
    var h = window.innerHeight;
    if (!h) return;
    drawer.style.setProperty('max-height', h + 'px', 'important');
    if (window.matchMedia && window.matchMedia('(min-width: 768px)').matches) {
      drawer.style.setProperty('height', h + 'px', 'important');
    } else {
      drawer.style.removeProperty('height');
    }
  }

  /* The checkout bar (.ajaxcart__footer-buttons) is position:fixed now, so
     it no longer occupies space in the scrollable form -- without this, the
     last item/trust content would render underneath it, permanently hidden.
     Measure the bar's real rendered height (it varies: one vs two lines of
     trust-row text, different font sizes per breakpoint) and pad the
     scroll region by exactly that much. */
  function syncCheckoutPadding() {
    var bar = document.querySelector('.js-drawer.drawer .ajaxcart__footer-buttons');
    var form = document.querySelector('.js-drawer.drawer form.ajaxcart');
    if (!bar || !form) return;
    var h = bar.offsetHeight;
    if (h > 0) form.style.paddingBottom = h + 'px';
  }

  (function () {
    var resizePending = null;
    function onResize() {
      if (resizePending) return;
      resizePending = window.requestAnimationFrame(function () {
        resizePending = null;
        syncDrawerMaxHeight();
        syncCheckoutPadding();
      });
    }
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
  })();

  function updateCartPageStickyBar() {
    if (!primaryGateActive) return; // no in-flow button to defer to -- leave renderSticky's own toggling alone
    var bar = document.querySelector('.aj-cart-sticky:not(.aj-cart-sticky--browse)');
    if (!bar) return;
    var hasItems = !!(lastCart && lastCart.item_count);
    if (hasItems && !primaryCheckoutVisible) {
      bar.removeAttribute('hidden');
    } else {
      bar.setAttribute('hidden', '');
    }
  }

  function watchPrimaryCheckout() {
    if (primaryGateActive || typeof window.IntersectionObserver === 'undefined') return;
    var primaryBtn = document.querySelector('.cart-modal__btn');
    if (!primaryBtn) return;
    primaryGateActive = true;
    new IntersectionObserver(function (entries) {
      primaryCheckoutVisible = entries[entries.length - 1].isIntersecting;
      updateCartPageStickyBar();
    }, { threshold: 0 }).observe(primaryBtn);
  }

  /* ------------------------------------------------------------------
     Clarity masking: let the replay show what the shopper actually saw.

     Clarity runs at its project-default "Balanced" masking, which renders
     every line-item title as blank and every input value as dots, so a
     drawer recording is unreadable exactly where the cart work happens.
     Unmasking the whole project would also record engraved initials, gift
     messages and the Special Instructions box, so masking stays on and
     these few PII-free nodes opt out individually.

     The static surfaces (ladder, sticky bar, drawer header) carry
     data-clarity-unmask in their own markup, which is the reliable place
     for it: the attribute is in the HTML before Clarity boots. Line items
     are written by the vendor ajaxcart (assets/wpbingo.min.js), which we
     do not edit, so they get stamped here instead, synchronously inside a
     MutationObserver callback. Clarity batches its own DOM serialization,
     so stamping in the same mutation-delivery turn lands before the node
     is recorded.

     Deliberately NOT listed, and they must stay that way: .ajaxcart__product-meta
     and .ajaxcart__product-metas (the engraved initial and message-card choice)
     and .ajaxcart__note-input (Special instructions). Those are customer-written
     text, and the whole point of keeping masking on is that they never leave
     the browser. */
  var UNMASK = [
    '.ajaxcart__product-name',  // line-item title
    '.ajaxcart__price',         // line-item price
    '.ajaxcart__qty-num'        // quantity input, the "•••••" in replays
  ].join(',');

  function unmaskCartText(root) {
    var nodes = (root || document).querySelectorAll(UNMASK);
    for (var i = 0; i < nodes.length; i++) {
      if (!nodes[i].hasAttribute('data-clarity-unmask')) {
        nodes[i].setAttribute('data-clarity-unmask', 'true');
      }
    }
  }

  function watchForUnmask(node) {
    if (!node || typeof window.MutationObserver === 'undefined') return;
    unmaskCartText(node);
    /* Undebounced on purpose -- unlike schedule(), which can wait 140ms for the
       cart to settle, this has to win a race against Clarity's serializer. */
    new MutationObserver(function () { unmaskCartText(node); })
      .observe(node, { childList: true, subtree: true });
  }

  function sync() {
    fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (cart) {
        if (!cart) return;
        lastCart = cart;
        var units = unitsOf(cart);
        renderLadder(units);
        renderUpsell(cart, units);
        renderSticky(cart);
        updateCartPageStickyBar();
        window.requestAnimationFrame(syncDrawerInnerOffset);
        window.requestAnimationFrame(syncDrawerMaxHeight);
        window.requestAnimationFrame(syncCheckoutPadding);
      })
      .catch(function () {});
  }

  function schedule() {
    if (pending) return;
    pending = window.setTimeout(function () {
      pending = null;
      sync();
    }, 140);
  }

  function observe(node) {
    if (!node || typeof window.MutationObserver === 'undefined') return;
    new MutationObserver(schedule).observe(node, { childList: true, subtree: true });
  }

  function init() {
    // The upsell block only ever ships alongside a ladder, so this gate covers both.
    if (!document.querySelector('[data-aj-ladder]')) return;
    sync();
    watchPrimaryCheckout();

    // ajaxCartPage re-renders this on every cart-page mutation; the drawer's
    // own container is re-rendered by ajaxCart on add/change/remove.
    observe(el('[data-cartpage-container]'));
    observe(el('[data-cart-container]'));

    watchForUnmask(el('[data-cartpage-container]'));
    watchForUnmask(el('[data-cart-container]'));

    // Opening the drawer does not necessarily mutate its item list (an
    // unchanged cart re-renders identical markup), so re-sync on open too.
    var drawer = el('.js-drawer');
    if (drawer && typeof window.MutationObserver !== 'undefined') {
      new MutationObserver(schedule).observe(drawer, {
        attributes: true,
        attributeFilter: ['class']
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})(this, document);
